import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Col, Row, Card } from "react-bootstrap";
import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Aos from "aos";
import "aos/dist/aos.css";
import Footer from "./Footer";
import Navbarsticky from "./NavbarSticky";
import { gettravelPackageList } from "../store/slices/tourpackage";
import Skeleton from "react-loading-skeleton";
import Slider from "rc-slider";
import { Sheet } from "react-modal-sheet";
import { OverlayTrigger, Popover } from "react-bootstrap";

const RATINGS = [4, 4.5, 5];

const getRandomRating = () =>
  RATINGS[Math.floor(Math.random() * RATINGS.length)];

const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 !== 0;

  return (
    <>
      {[...Array(fullStars)].map((_, i) => (
        <i key={`full-${i}`} className="bi bi-star-fill text-warning me-1"></i>
      ))}

      {halfStar && <i className="bi bi-star-half text-warning me-1"></i>}

      {[...Array(5 - Math.ceil(rating))].map((_, i) => (
        <i key={`empty-${i}`} className="bi bi-star text-warning me-1"></i>
      ))}
    </>
  );
};

export default function TourPackage() {
  const dispatch = useDispatch();
  const { categorySlug } = useParams();
  const [isDragging, setIsDragging] = useState(false);

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [value, setValue] = useState([0, 150000]);
  const [duration, setDuration] = useState("");
  const [sortBy, setSortBy] = useState("default");

  const [page, setPage] = useState(1);
  const [packages, setPackages] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isOpen, setOpen] = useState(false);

  const loaderRef = useRef(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const durationOptions = [
    "3 nights 4 days",
    "4 nights 5 days",
    "5 nights 6 days",
    "6 nights 7 days",
    "7 nights 8 days",
    "8 nights 9 days",
    "9 nights 10 days",
    "10 nights 11 days",
    "11 nights 12 days",
    "12 nights 13 days",
    "13 nights 14 days",
    "14 nights 15 days",
  ];

  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
  }, [isLoading, hasMore]);

  useEffect(() => {
    Aos.init({ duration: 900, once: true });
  }, []);

  // ========================
  // BUILD FILTERS (CORRECT)
  // ========================
  const buildFilters = useCallback(() => {
    const filters = { page, limit: 6 };

    // categorySlug ONLY if filters not applied
    if (categorySlug && !isFilterApplied) {
      filters.categorySlug = categorySlug;
    }

    if (!(value[0] === 0 && value[1] === 150000)) {
      filters.priceRange = value.map((v) => v * 2);
    }

    if (duration) {
      filters.duration = duration;
    }

    return filters;
  }, [page, categorySlug, value, duration, isFilterApplied]);

  // ========================
  // API CALL
  // ========================
  const loadPackages = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) return;

    setIsLoading(true);

    try {
      const res = await dispatch(gettravelPackageList(buildFilters())).unwrap();
      const items = res?.packages?.packageData || [];

      const enrichedItems = items.map((item) => ({
        ...item,
        rating: getRandomRating(),
      }));

      setPackages((prev) =>
        page === 1 ? enrichedItems : [...prev, ...enrichedItems]
      );

      setHasMore(items.length === 6);
    } catch (err) {
      console.error(err);
    }

    setIsLoading(false);
  }, [dispatch, buildFilters, page]);

  //  SINGLE API EFFECT (FIXED)
  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    if (isDragging) return;

    setPage(1);
    setHasMore(true);
    setPackages([]);
  }, [categorySlug, value, duration, isDragging]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMoreRef.current &&
        !isLoadingRef.current
      ) {
        setPage((p) => p + 1);
      }
    });

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, []);

  // Sorting
  const sortedPackages = [...packages].sort((a, b) => {
    if (sortBy === "price_asc") return a.packagebaseRate - b.packagebaseRate;
    if (sortBy === "price_desc") return b.packagebaseRate - a.packagebaseRate;
    return 0;
  });

  return (
    <>
      <Navbarsticky />
      <section className="page-container hero-section text-center">
        <h1 className="fw-bold text-white hero-title">Andaman Island Escape</h1>
        <p className="text-white hero-subtitle">
          Unwind amid crystal-clear seas and serene white shores.
        </p>
      </section>
      <section className="container p-4 my-4">
        <div className="row g-4">
          <div className="col-12 col-md-4 col-lg-3 d-none d-md-none d-lg-block d-xl-block d-xxl-block">
            <div className="filter-box p-4 shadow-sm">
              <div className="d-flex justify-content-between">
                <h5 className="fw-bold">Filter</h5>
                <button
                  className="clear-btn"
                  onClick={() => {
                    setIsFilterApplied(false);
                    setValue([0, 150000]);
                    setDuration("");
                    setSortBy("default");
                    setPage(1);
                    setPackages([]);
                    setHasMore(true);
                  }}
                >
                  Clear All
                </button>
              </div>

              <h5 className="mt-3">Budget</h5>
              <Slider
                range
                min={0}
                max={150000}
                value={value}
                allowCross={false}
                onBeforeChange={() => setIsDragging(true)}
                onChange={(v) => setValue(v)}
                onAfterChange={(v) => {
                  setIsDragging(false);
                  setValue(v);
                  setIsFilterApplied(true);
                }}
              />

              <p className="fw-bold mt-2">
                ₹ {value[0]} - ₹ {value[1]}
              </p>

              <hr />

              <h5>Duration</h5>
              {durationOptions.map((d, i) => (
                <div
                  key={i}
                  className="d-flex align-items-center mb-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setDuration(d);
                    setIsFilterApplied(true);
                  }}
                >
                  <Form.Check type="radio" checked={duration === d} readOnly />
                  <span className="ms-2">{d}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="col-12 col-md-12 col-lg-9">
            <div className="d-flex justify-content-end align-items-center mb-4 flex-wrap">
              <h6 className="fw-semibold me-2">
                Showing {sortedPackages.length} Results
              </h6>
              <button
                className="btn btn-outline-primary px-3 mt-2 py-1 d-lg-none d-xl-none d-xxl-none me-2 mb-2"
                onClick={() => setOpen(true)}
              >
                Filters <i className="bi bi-filter ms-1"></i>
              </button>

              <DropdownButton
                title={
                  sortBy === "price_asc"
                    ? "Price ↑"
                    : sortBy === "price_desc"
                    ? "Price ↓"
                    : "Default"
                }
              >
                <Dropdown.Item
                  onClick={() => setSortBy("default")}
                  className="dropdown-item-black"
                >
                  Default
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setSortBy("price_asc")}
                  className="dropdown-item-black"
                >
                  Price: Low → High
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setSortBy("price_desc")}
                  className="dropdown-item-black"
                >
                  Price: High → Low
                </Dropdown.Item>
              </DropdownButton>
            </div>

            <Row xs={2} sm={2} lg={2} className="g-2 g-lg-4">
              {isLoading && page === 1
                ? Array.from({ length: 6 }).map((_, i) => (
                    <Col key={i}>
                      <div className="destination-item bgc-lighter">
                        <Skeleton height={200} borderRadius="8px" />
                        <Skeleton count={3} className="mt-2" />
                      </div>
                    </Col>
                  ))
                : sortedPackages.map((item, index) => (
                    <Col key={index}>
                      <Link to={`/package-details/${item.slug}`}>
                        <Card className="tour-card shadow-sm">
                          <img
                            src={item.images[0]?.mediaLink}
                            className="tour-img"
                            alt=""
                          />

                          <Card.Body>
                            <Card.Title
                              style={{ fontSize: "17px", fontWeight: "bold" }}
                            >
                              {item.packageName}
                            </Card.Title>

                            {(() => {
                              const pop = (
                                <Popover
                                  id="place-popover"
                                  style={{
                                    maxWidth: "260px",
                                    borderRadius: "12px",
                                  }}
                                >
                                  <Popover.Body>
                                    <div className="fw-bold mb-2">
                                      {item.stateName ||
                                        "Andaman & Nicobar Islands"}
                                    </div>

                                    <ul
                                      className="list-unstyled mb-0"
                                      style={{ paddingLeft: "12px" }}
                                    >
                                      {item.hotelDetails?.map((p, idx) => (
                                        <li key={idx} className="text-muted">
                                          {p.place}{" "}
                                          {p.duration ? `(${p.duration}N)` : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  </Popover.Body>
                                </Popover>
                              );

                              return (
                                <OverlayTrigger
                                  placement="bottom"
                                  trigger={["hover", "focus"]}
                                  overlay={pop}
                                >
                                  <Card.Text
                                    className="text-muted"
                                    style={{
                                      fontSize: "13px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <i className="bi bi-geo-alt-fill text-primary me-1"></i>

                                    {(() => {
                                      const names =
                                        item.places?.map((p) => p.name) || [];
                                      const firstTwo = names
                                        .slice(0, 2)
                                        .join(", ");
                                      const remaining =
                                        names.length > 2
                                          ? ` + ${names.length - 2} more places`
                                          : "";
                                      return firstTwo + remaining;
                                    })()}
                                  </Card.Text>
                                </OverlayTrigger>
                              );
                            })()}

                            <Card.Text className="text-muted">
                              <i className="bi bi-calendar-event text-primary me-1"></i>
                              {item.duration}
                              <div className="rating mt-2 d-flex d-lg-none d-md-none align-items-center">
                                {renderStars(item.rating)}
                                <span className="text-muted ms-1">
                                  ({item.rating})
                                </span>
                              </div>
                            </Card.Text>

                            <div className=" d-flex justify-content-between align-items-center">
                              <div className="price-box">
                                <span className="price">
                                  ₹ {Math.round(item.packagebaseRate / 2)}
                                </span>
                                <span className="per-person">/Person</span>
                              </div>

                              <div className="rating d-none d-lg-flex d-md-flex align-items-center">
                                {renderStars(item.rating)}
                                <span className="text-muted ms-1">
                                  ({item.rating})
                                </span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                    </Col>
                  ))}

              <div ref={loaderRef} style={{ height: "20px" }} />
              {isLoading && page > 1 && (
                <div className="col-12 text-center">
                  <Skeleton width={200} height={20} />
                </div>
              )}
            </Row>
          </div>
        </div>
      </section>
      <Sheet isOpen={isOpen} onClose={() => setOpen(false)}>
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content>
            <div className="p-4">
              <h5 className="fw-bold mb-3">Budget</h5>
              <Slider
                range
                min={0}
                max={150000}
                value={value}
                allowCross={false}
                onBeforeChange={() => setIsDragging(true)}
                onChange={(v) => setValue(v)}
                onAfterChange={(v) => {
                  setIsDragging(false);
                  setValue(v);
                  setIsFilterApplied(true);
                }}
              />

              <p className="fw-bold">
                ₹ {value[0]} - ₹ {value[1]}
              </p>

              <hr />

              <h5 className="fw-bold mb-3">Duration</h5>
              {[
                "3 nights 4 days",
                "4 nights 5 days",
                "5 nights 6 days",
                "6 nights 7 days",
                "7 nights 8 days",
                "8 nights 9 days",
                "9 nights 10 days",
                "10 nights 11 days",
                "11 nights 12 days",
                "12 nights 13 days",
                "13 nights 14 days",
                "14 nights 15 days",
              ].map((d, i) => (
                <Form.Check
                  key={i}
                  type="radio"
                  label={d}
                  checked={duration === d}
                  onChange={() => setDuration(d)}
                />
              ))}

              <button
                onClick={() => setOpen(false)}
                className="take-tour-btn mt-4 px-4 py-2 w-100"
              >
                Apply Filter <i className="bi bi-arrow-right ms-2" />
              </button>
            </div>
          </Sheet.Content>
        </Sheet.Container>

        <Sheet.Backdrop />
      </Sheet>
      <Footer />
    </>
  );
}
